// ABOUTME: Public landing page — the marketing home page for Daubly.
// ABOUTME: Presents the product value proposition and funnels visitors to sign up.
import { useEffect } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import { SignInButton, useAuth } from '@clerk/react'
import { Link } from 'react-router'
import { trackLandingPageViewed } from '../analytics/events'
import { HeroSection, MockBingoCell, FeatureCard, LandingSection, UseCaseGrid, UseCaseTile, CtaSection, CtaSupportText } from '../theme'

const BENEFITS = [
  {
    emoji: '⚡',
    heading: 'Real-time play',
    body: 'Every card updates the instant something gets called.',
  },
  {
    emoji: '📱',
    heading: 'No app needed',
    body: "Share a link and everyone's in. No app, no account, nothing to install.",
  },
  {
    emoji: '🎯',
    heading: 'Custom or classic',
    body: 'Go classic with numbers, or fill the squares with whatever fits your crowd.',
  },
]

const USE_CASES = [
  { emoji: '🏢', label: 'Work meetings' },
  { emoji: '🏈', label: 'Sports' },
  { emoji: '🎵', label: 'Concerts' },
  { emoji: '🎉', label: 'Parties' },
  { emoji: '💻', label: 'Virtual hangouts' },
  { emoji: '🎓', label: 'Classrooms' },
]

// 5x5 grid — true = daubed (teal), false = empty
const MOCK_CARD: boolean[][] = [
  [false, true,  false, true,  false],
  [true,  false, false, false, true ],
  [false, false, true,  false, false],
  [true,  false, false, false, true ],
  [false, true,  false, true,  false],
]

function MockBingoCard() {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(5, 1fr)"
      gap={1}
      maxWidth={280}
      margin="0 auto"
    >
      {MOCK_CARD.flat().map((daubed, i) => (
        <MockBingoCell key={i} daubed={daubed} />
      ))}
    </Box>
  )
}

function GetStartedButton({ color }: { color: 'primary' | 'secondary' }) {
  const { isSignedIn } = useAuth()
  if (isSignedIn) {
    return (
      <Button component={Link} to="/games/new" variant="contained" color={color} size="large">
        Create Free Game
      </Button>
    )
  }
  return (
    <SignInButton forceRedirectUrl="/games/new">
      <Button variant="contained" color={color} size="large">
        Create Free Game
      </Button>
    </SignInButton>
  )
}

export function LandingPage() {
  useEffect(() => {
    trackLandingPageViewed()
  }, [])

  return (
    <>
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h1" gutterBottom>
                Turn any event into a game.
              </Typography>
              <Typography variant="h5" color="text.secondary" gutterBottom>
                Real-time bingo for any group, any occasion. Go with classic Bingo, or
                create your own squares.
              </Typography>
              <Box mt={4}>
                <GetStartedButton color="primary" />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MockBingoCard />
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <LandingSection>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {BENEFITS.map((benefit) => (
              <Grid size={{ xs: 12, md: 4 }} key={benefit.heading}>
                <FeatureCard>
                  <Typography variant="h3" component="div" mb={1}>
                    {benefit.emoji}
                  </Typography>
                  <Typography variant="h5" gutterBottom>
                    {benefit.heading}
                  </Typography>
                  <Typography color="text.secondary">{benefit.body}</Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </LandingSection>

      <LandingSection>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom>
            Made for Any Occasion
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" mb={4}>
            From staff meetings to stadium nights, there's a bingo card for it.
          </Typography>
          <UseCaseGrid>
            {USE_CASES.map((uc) => (
              <UseCaseTile key={uc.label}>
                <Typography variant="h4" component="div">
                  {uc.emoji}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {uc.label}
                </Typography>
              </UseCaseTile>
            ))}
          </UseCaseGrid>
        </Container>
      </LandingSection>

      <CtaSection>
        <Container maxWidth="sm">
          <Typography variant="h3" gutterBottom>
            Ready to play?
          </Typography>
          <CtaSupportText>Free to play. No credit card required.</CtaSupportText>
          <GetStartedButton color="secondary" />
        </Container>
      </CtaSection>
    </>
  )
}
